import { useState, useEffect } from "react";
import { Box, Button, TextField, Tooltip, Typography } from "@mui/material";
import { RatingTypography } from "components";
import { filter, MAX_DESCRIPTION_LENGTH } from "constants";

export const ReviewInput = ({
  formData,
  setFormData,
  onSubmit,
  isSubmitting,
  isEditing = false,
  onCancel = null,
  secondsRemaining,
  fetchingLimit,
}) => {
  const [containsProfanity, setContainsProfanity] = useState(false);

  useEffect(() => {
    setContainsProfanity(filter.isProfane(formData.description));
  }, [formData.description]);

  const handleRatingChange = (newRating) => {
    setFormData((prev) => ({ ...prev, rating: newRating }));
  };

  const handleTextChange = (newText) => {
    setFormData((prev) => ({
      ...prev,
      description: newText.slice(0, MAX_DESCRIPTION_LENGTH),
    }));
  };

  const charCount = formData.description.length;
  const isOverLimit = charCount >= MAX_DESCRIPTION_LENGTH;

  const content = (
    <Box display="flex" flexDirection="column" gap={1}>
      <RatingTypography rating={formData.rating} onChange={handleRatingChange} editable size="large" />
      <TextField
        multiline
        minRows={isEditing ? 2 : 3}
        value={formData.description}
        onChange={(e) => handleTextChange(e.target.value)}
        fullWidth
        variant="outlined"
        placeholder={isEditing ? "Edit your review..." : "Please share a short review..."}
        error={containsProfanity || isOverLimit}
        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
        slotProps={{ input: { maxLength: MAX_DESCRIPTION_LENGTH } }}
        helperText={
          containsProfanity ? (
            "Your review contains inappropriate language."
          ) : (
            <Typography
              variant="caption"
              color={isOverLimit ? "error" : "text.secondary"}
              component="span"
              sx={{ display: "flex", justifyContent: "flex-end" }}
            >
              {charCount} / {MAX_DESCRIPTION_LENGTH}
            </Typography>
          )
        }
      />
      <Box display="flex" justifyContent="flex-end" gap={1}>
        {isEditing && (
          <Button variant="outlined" onClick={onCancel} sx={{ borderRadius: "8px" }}>
            Cancel
          </Button>
        )}
        <Tooltip
          title={secondsRemaining > 0 ? `Please wait ${secondsRemaining}s before posting another review.` : ""}
          arrow
        >
          <span style={{ cursor: secondsRemaining > 0 || isSubmitting || fetchingLimit ? "not-allowed" : "pointer" }}>
            <Button
              variant="contained"
              onClick={onSubmit}
              disabled={
                formData.rating <= 0 ||
                isSubmitting ||
                containsProfanity ||
                isOverLimit ||
                fetchingLimit ||
                secondsRemaining > 0
              }
              sx={{ borderRadius: "8px" }}
            >
              {isSubmitting ? "Saving..." : "Save"}
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );

  return isEditing ? content : <form onSubmit={onSubmit}>{content}</form>;
};